import { useCallback, useMemo, useRef } from 'react';
import { store } from '../data/store';
import { serverNow } from '../data/binance';
import { C, F } from '../styles/palette';
import { crisp, type CanvasSurface } from '../lib/frame';
import { compact, hmsMilli, price as fmtPrice, qty as fmtQty } from '../lib/num';
import type { TradeRing } from '../lib/ring';
import { useCanvasPanel, usePersisted, useStoreTick, useStructure } from './hooks';

const ROW_H = 20;
const HEADER_H = 16;
/** How long a print stays highlighted as "just happened". */
const FRESH_MS = 400;

/**
 * Time and sales.
 *
 * The tape is the highest-rate surface in the terminal — BTCUSDT alone can
 * print several hundred times a second — so it reads straight out of the trade
 * ring buffer and paints only the rows that fit on screen. Nothing is
 * allocated per row and nothing is stored per row beyond what the ring already
 * holds.
 *
 * "Large print" highlighting is relative, not absolute: a 5 BTC trade and a
 * 5,000 DOGE trade are both unremarkable, so the threshold is a multiple of
 * the median size in the visible window, recomputed as the window scrolls.
 */
export function TapePanel() {
  useStructure();
  useStoreTick();

  const [minSize, setMinSize] = usePersisted<number>('terminal.tape.min', 0);
  const [showTime, setShowTime] = usePersisted<boolean>('terminal.tape.time', true);

  const symbol = store.active;
  const st = store.get(symbol);
  const ring = st.trades;

  const lastSeq = useRef(-1);
  const lastSymbol = useRef('');
  const stats = useRef({ buy: 0, sell: 0, count: 0, median: 0 });

  const draw = useCallback(
    (surface: CanvasSurface) => {
      const { ctx, width, height } = surface;
      drawTape(ctx, width, height, ring, {
        precision: st.pricePrecision,
        minSize,
        showTime,
        stats: stats.current,
      });
      lastSeq.current = ring.seq;
      lastSymbol.current = symbol;
    },
    [ring, st.pricePrecision, minSize, showTime, symbol],
  );

  const canvasRef = useCanvasPanel(draw, {
    priority: 6,
    dirty: () => ring.seq !== lastSeq.current || symbol !== lastSymbol.current,
  });

  // Buy/sell split over the recent window, refreshed on the store's UI tick
  // rather than per print.
  const flow = useMemo(() => {
    const n = Math.min(ring.size, 500);
    let buy = 0;
    let sell = 0;
    for (let i = 0; i < n; i++) {
      const q = ring.qtyAt(i);
      if (ring.sideAt(i) === 1) buy += q;
      else sell += q;
    }
    stats.current.buy = buy;
    stats.current.sell = sell;
    stats.current.count = n;
    return { buy, sell, total: buy + sell };
  }, [ring, ring.seq]);

  const buyPct = flow.total > 0 ? (flow.buy / flow.total) * 100 : 50;

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">Time &amp; sales</span>
        <span className="sub">{symbol}</span>
        <span className="spacer" />
        <button
          className={`btn sm ghost ${showTime ? 'active' : ''}`}
          onClick={() => setShowTime(!showTime)}
          title="Show timestamps"
        >
          ⏱
        </button>
        <input
          className="input tape-filter num"
          type="number"
          min={0}
          step="any"
          value={minSize || ''}
          placeholder="min size"
          title="Hide prints smaller than this"
          onChange={(e) => setMinSize(Number(e.target.value) || 0)}
        />
      </div>

      <div className="panel-body">
        <canvas ref={canvasRef} className="panel-canvas" />
        {ring.size === 0 && <div className="placeholder">awaiting prints</div>}
      </div>

      <div className="tape-foot" title="Aggressor split over the last 500 prints">
        <div className="tape-flow">
          <div className="tape-flow-buy" style={{ width: `${buyPct.toFixed(1)}%` }} />
        </div>
        <span className="up num">{compact(flow.buy)}</span>
        <span className="muted num">{buyPct.toFixed(0)}%</span>
        <span className="down num">{compact(flow.sell)}</span>
      </div>
    </div>
  );
}

interface TapeOpts {
  precision: number;
  minSize: number;
  showTime: boolean;
  stats: { buy: number; sell: number; count: number; median: number };
}

function drawTape(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  ring: TradeRing,
  opts: TapeOpts,
): void {
  ctx.fillStyle = C.bg1;
  ctx.fillRect(0, 0, width, height);

  const rows = Math.max(0, Math.floor((height - HEADER_H) / ROW_H));
  if (rows === 0 || ring.size === 0) return;

  const padR = 8;
  const colPrice = 8;
  const colTime = width - padR;
  const colSize = opts.showTime ? colTime - 66 : colTime;

  ctx.font = F.micro;
  ctx.fillStyle = C.text3;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText('PRICE', colPrice, HEADER_H / 2);
  ctx.textAlign = 'right';
  ctx.fillText('SIZE', colSize, HEADER_H / 2);
  if (opts.showTime) ctx.fillText('TIME', colTime, HEADER_H / 2);
  ctx.strokeStyle = C.line;
  ctx.beginPath();
  ctx.moveTo(0, crisp(HEADER_H));
  ctx.lineTo(width, crisp(HEADER_H));
  ctx.stroke();

  // Collect the visible prints first (the filter means row N is not print N),
  // and take a median of their sizes for the "large print" threshold.
  const idx: number[] = [];
  const sizes: number[] = [];
  for (let i = 0; i < ring.size && idx.length < rows; i++) {
    const q = ring.qtyAt(i);
    if (opts.minSize > 0 && q < opts.minSize) continue;
    idx.push(i);
    sizes.push(q);
  }
  if (idx.length === 0) return;

  const sorted = [...sizes].sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * q))];
  opts.stats.median = sorted[sorted.length >> 1] || 0;

  // Percentiles, not multiples of the median. On a book thick with dust the
  // median is itself dust, so "6x the median" is an ordinary trade and almost
  // every row lights up — which is exactly what a flat threshold got wrong.
  // The top ~12% and top ~3% of the visible window are what a trader means by
  // a large print, whatever the symbol's typical size happens to be.
  const bigThreshold = at(0.88);
  const hugeThreshold = at(0.97);
  const maxSize = sorted[sorted.length - 1] || 1;

  // Trade times come from the exchange clock. Using the local one here made
  // `age` hugely negative on a skewed machine, which drove the freshness
  // alpha past 1 and painted every row a solid block of colour.
  const now = serverNow();

  for (let r = 0; r < idx.length; r++) {
    const i = idx[r];
    const y = HEADER_H + r * ROW_H;
    const p = ring.priceAt(i);
    const q = ring.qtyAt(i);
    const t = ring.timeAt(i);
    const isBuy = ring.sideAt(i) === 1;

    const col = isBuy ? C.up : C.down;

    // Faint zebra banding keeps long runs of one colour readable.
    if (r % 2 === 1) {
      ctx.fillStyle = 'rgba(255,255,255,0.014)';
      ctx.fillRect(0, y, width, ROW_H - 1);
    }

    // Size bar behind the row, so relative size reads without parsing digits.
    const frac = maxSize > 0 ? Math.min(1, q / maxSize) : 0;
    if (frac > 0.02) {
      ctx.fillStyle = isBuy ? C.upWash : C.downWash;
      const w = frac * width * 0.5;
      ctx.fillRect(width - w, y, w, ROW_H - 1);
    }

    // Fresh prints glow briefly so the eye catches new activity. Clamped at
    // both ends: a negative age (clock skew, or a print stamped slightly in
    // the future) must not produce an alpha above the intended peak.
    const age = Math.max(0, now - t);
    if (age < FRESH_MS) {
      ctx.globalAlpha = 0.15 * (1 - age / FRESH_MS);
      ctx.fillStyle = col;
      ctx.fillRect(0, y, width, ROW_H - 1);
      ctx.globalAlpha = 1;
    }

    if (q >= hugeThreshold) {
      ctx.fillStyle = isBuy ? 'rgba(46,189,133,0.13)' : 'rgba(246,70,93,0.13)';
      ctx.fillRect(0, y, width, ROW_H - 1);
      ctx.fillStyle = col;
      ctx.fillRect(0, y, 2, ROW_H - 1);
    } else if (q >= bigThreshold) {
      ctx.fillStyle = col;
      ctx.globalAlpha = 0.6;
      ctx.fillRect(0, y, 2, ROW_H - 1);
      ctx.globalAlpha = 1;
    }

    const mid = y + ROW_H / 2 - 0.5;
    ctx.font = q >= bigThreshold ? F.xsBold : F.xs;
    ctx.textAlign = 'left';
    ctx.fillStyle = col;
    ctx.fillText(fmtPrice(p, opts.precision), colPrice, mid);

    ctx.textAlign = 'right';
    ctx.fillStyle = q >= bigThreshold ? C.text0 : C.text1;
    ctx.fillText(fmtQty(q), colSize, mid);

    if (opts.showTime) {
      ctx.fillStyle = C.text3;
      ctx.font = F.micro;
      ctx.fillText(hmsMilli(t), colTime, mid);
    }
  }
}
